import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { Client } from '@/models/Client';
import { useClientUpcomingBookings } from '@/hooks/useClientUpcomingBookings';
import { formatUpcomingBookingDate } from '@/utils/dateHelpers';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ClientUpcomingBookingsDialogProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ClientUpcomingBookingsDialog: React.FC<ClientUpcomingBookingsDialogProps> = ({
  client,
  open,
  onOpenChange,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const { data: allUpcomingBookings = [], isLoading } = useClientUpcomingBookings(client?.id || '');

  const handleBookingClick = (booking: any) => {
    // Set calendar filters in localStorage before navigation (organization-specific)
    if (currentOrganization?.id) {
      const filters = {
        selectedDate: booking.booking_date,
        selectedWorkoutTypes: [booking.workout_type_id], // Focus on this specific workout type
      };
      
      const storageKey = `fitness_app_calendar_filters_${currentOrganization.id}`;
      localStorage.setItem(storageKey, JSON.stringify(filters));
    }
    
    // Close the dialog first
    onOpenChange(false);
    
    // Navigate to calendar with additional params for scrolling
    navigate(`/calendar?date=${booking.booking_date}&timeSlot=${encodeURIComponent(booking.time_slot)}&workoutType=${booking.workout_type_id}`);
  };

  if (!client) return null;

  const content = (
    <div className="space-y-4">
      {isLoading ? (
        <div className="text-center text-muted-foreground py-8">
          {t('common.loading')}...
        </div>
      ) : allUpcomingBookings.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {t('clients.noUpcomingBookings')}
        </div>
      ) : (
        <div className="space-y-3">
          {allUpcomingBookings.map((booking) => (
            <div
              key={booking.id}
              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
              onClick={() => handleBookingClick(booking)}
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <span className="font-medium">
                    {formatUpcomingBookingDate(booking.booking_date, i18n.language)}
                  </span>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {booking.time_slot}
                  </div>
                  {booking.booking_type === 'recurring' && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {t('calendar.recurring')}
                    </div>
                  )}
                </div>
                <Badge
                  variant="secondary"
                  style={{
                    backgroundColor: booking.workout_type?.color || '#64748b',
                    color: 'white'
                  }}
                >
                  {booking.workout_type?.name}
                </Badge>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (isFullScreen) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent 
          side="bottom" 
          className={`${isTablet ? 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix' : 'h-[80vh] w-full rounded-t-xl border-0 p-0 flex flex-col safe-area-aware ios-modal-safe pwa-modal-header-fix pt-safe-top pb-safe-bottom'}`}
        >
          <SheetHeader className={`${isTablet ? 'p-6 border-b flex-shrink-0' : 'p-4 pb-2 border-b flex-shrink-0 pt-[calc(2rem+env(safe-area-inset-top))] pwa-safe-header ios-sheet-header safe-top'}`}>
            <SheetTitle className="text-lg greek-text flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t('clients.upcomingBookings')} - {client.firstName} {client.lastName}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">
            <ScrollArea className="h-full">
              <div className={`${isTablet ? 'p-6' : 'p-4'}`}>
                {content}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl w-[90vw] max-h-[85vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="p-6 pb-4 border-b flex-shrink-0">
            <DialogTitle className="text-xl greek-text flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t('clients.upcomingBookings')} - {client.firstName} {client.lastName}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            <ScrollArea className="h-full">
              <div className="p-6 pt-4">
                {content}
              </div>
            </ScrollArea>
          </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientUpcomingBookingsDialog;